import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * ChangeSiloModal — Supervisor swaps the source silo mid-export.
 * Server auto-snapshots the previous segment.
 */
export default function ChangeSiloModal({ exportOp, onClose, onSuccess }) {
    const [silos, setSilos] = useState([]);
    const [siloNo, setSiloNo] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/tronox/silos");
                setSilos(r.data?.silos || []);
            } catch (e) { toast.error(formatApiError(e)); }
            setLoading(false);
        })();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!siloNo) return toast.error("Choose the new silo.");
        if (Number(siloNo) === exportOp.current_silo_no) return toast.error("Already loading from this silo.");
        setBusy(true);
        try {
            await api.post(`/tronox/exports/${exportOp.id}/change-silo`, { silo_no: Number(siloNo) });
            toast.success(`Switched to Silo ${siloNo}.`);
            onSuccess();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="change-silo-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-md border border-slate-700 shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-400">Load-Out Station</div>
                        <div className="text-lg font-black">Switch source silo</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-change-silo">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-4">
                    <div className="text-xs text-slate-400">
                        Currently loading from <b className="text-teal-300">Silo {exportOp.current_silo_no}</b>.
                        Switching will snapshot the current silo&apos;s contribution and start a new segment.
                    </div>
                    {loading ? (
                        <div className="text-center text-slate-400 py-8">Loading silos…</div>
                    ) : (
                        <>
                            <div>
                                <label className="text-[11px] uppercase text-slate-400 tracking-wider">New silo</label>
                                <select
                                    value={siloNo}
                                    onChange={(e) => setSiloNo(e.target.value)}
                                    className="g-input mt-1"
                                    data-testid="change-silo-select"
                                >
                                    <option value="">— Choose a silo —</option>
                                    {silos.map((s) => {
                                        const isCurrent = s.silo_no === exportOp.current_silo_no;
                                        const empty = s.current_level_tons <= 0;
                                        const vesselProduct = (exportOp.product || "").trim().toLowerCase();
                                        const productMismatch = !empty && vesselProduct && (s.product || "").trim().toLowerCase() !== vesselProduct;
                                        const disabled = isCurrent || empty || productMismatch;
                                        const tag = isCurrent ? " (current)" : empty ? " (empty)" : productMismatch ? ` (product mismatch — ${s.product})` : "";
                                        return (
                                            <option key={s.silo_no} value={s.silo_no} disabled={disabled}>
                                                {`Silo ${s.silo_no} · ${s.product} · ${Math.round(s.current_level_tons)} t on hand${tag}`}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={onClose} className="flex-1 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold" data-testid="change-silo-cancel">Cancel</button>
                                <button type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 text-white font-black" data-testid="change-silo-submit">
                                    <ArrowsClockwise size={16} weight="bold" /> {busy ? "Switching…" : "Switch silo"}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}
